import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { AgentEvents } from '@dreamai/types';
import api from '../../../api';
import { usePusher } from '../../../contexts/PusherContext';
import { useProjectAppLoading } from '../../../providers/ProjectAppViewLoadingProvider';
import ArtifactOpener from './ArtifactOpener';
import {
  buildGeneratedPageBridge,
  themeGeneratedPage,
  wrapHtmlWithBridge,
} from './generatedPageBridge';
import ProjectAppEmptyState from './ProjectAppEmptyState';
import './project-app-generated-page.scss';

interface ProjectAppGeneratedPageProps {
  appId: string;
  slug: string;
  /**
   * Render a tab bar across the app's views and let the user switch between
   * them. Used by the home-screen Global Apps view, which has no other tab
   * chrome. Off by default so the project canvas host (which supplies its own
   * tabs) is unaffected.
   */
  showViewTabs?: boolean;
}

interface ViewResponse {
  success: boolean;
  appId: string;
  slug: string;
  name: string;
  version: number;
  html: string;
}

interface ViewUpdatedEvent {
  threadId: string;
  appId: string;
  slug: string;
  version: number;
  timestamp: string;
}

interface ViewSummary {
  slug: string;
  name: string;
  version: number;
}

interface ViewsResponse {
  success: boolean;
  appId: string;
  homeViewSlug: string | null;
  views: ViewSummary[];
}

/**
 * Renders a generated project app view inside a sandboxed iframe.
 *
 * Wires up a postMessage bridge so the generated page can:
 *   - receive an auth token (window.__DREAM_AUTH_TOKEN__ + 'dream-ready' event)
 *   - navigate the parent app via { type: 'navigate', to: '/...' }
 *
 * The iframe scrolls its own content (no auto-resize) so the surrounding
 * frame stays put.
 *
 * Refetches the HTML when the PVA pushes PROJECT_APP_VIEW_UPDATED for this slug.
 */
const ProjectAppGeneratedPage: React.FC<ProjectAppGeneratedPageProps> = ({
  appId,
  slug,
  showViewTabs = false,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const pusherContext = usePusher(true);
  const navigate = useNavigate();
  const { finishedKey } = useProjectAppLoading();

  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Only the latest view fetch may write state.
  const viewRequestRef = useRef(0);
  const [view, setView] = useState<ViewResponse | null>(null);
  // The app's views, rendered as a tab bar when there is more than one. The
  // home view is returned first by the backend.
  const [views, setViews] = useState<ViewSummary[]>([]);
  // The slug currently displayed. Starts at the home slug passed in and
  // changes when the user picks another tab.
  const [activeSlug, setActiveSlug] = useState(slug);
  const [loading, setLoading] = useState(true);
  // True only once the iframe's onLoad has fired for the current srcDoc.
  // Until then we keep the loading overlay visible and hide the iframe to
  // prevent flashes of unstyled HTML before the page's <style>/<link> tags
  // have been parsed and applied.
  const [iframeReady, setIframeReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Generated pages paint their own loading skeleton as soon as the document
  // parses, so the host loader stays blank for a beat and only falls back to a
  // spinner when the fetch/parse is genuinely slow.
  const [slowLoad, setSlowLoad] = useState(false);
  // Set when the iframe posts `dream-open-artifact` — drives the modal
  // mount/unmount lifecycle so closing returns control to the iframe.
  const [openArtifactId, setOpenArtifactId] = useState<string | null>(null);

  const apiBase = process.env.REACT_APP_API_URL || '';

  // Reset to the home slug whenever the app (or its home view) changes so a
  // stale tab selection never leaks between apps.
  useEffect(() => {
    setActiveSlug(slug);
  }, [slug]);

  // ── Build the runtime bridge script injected into the iframe ──
  const wrappedHtml = useMemo(() => {
    if (!view?.html) return '';
    const bridge = buildGeneratedPageBridge({
      apiBase,
      appId,
      slug: activeSlug,
    });
    return themeGeneratedPage(wrapHtmlWithBridge(view.html, bridge));
  }, [view?.html, apiBase, appId, activeSlug]);

  // ── Fetch the view HTML ──
  const fetchView = useCallback(async () => {
    const requestId = ++viewRequestRef.current;
    const isCurrent = () => requestId === viewRequestRef.current;
    try {
      setError(null);
      const token = await getAccessTokenSilently();
      const res = await api.get<ViewResponse>(
        `/project-apps/${appId}/view/${activeSlug}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!isCurrent()) return;
      if (res.data?.success) setView(res.data);
      else setError('Failed to load view');
    } catch (err: any) {
      if (!isCurrent()) return;
      const status = err?.response?.status;
      if (status === 404) {
        setError('not-found');
      } else {
        console.error('Error fetching project app view:', err);
        setError(err?.message || 'Failed to load view');
      }
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [appId, activeSlug, getAccessTokenSilently]);

  // ── Fetch the list of views (for the tab bar) ──
  const fetchViews = useCallback(async () => {
    if (!showViewTabs) return;
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<ViewsResponse>(`/project-apps/${appId}/views`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.success) setViews(res.data.views);
    } catch (err) {
      // A failed views fetch just means no tab bar — the single view still
      // renders, so swallow the error.
      console.error('Error fetching project app views:', err);
    }
  }, [appId, showViewTabs, getAccessTokenSilently]);

  useEffect(() => {
    fetchViews();
  }, [fetchViews]);

  useEffect(() => {
    setLoading(true);
    setIframeReady(false);
    fetchView();
  }, [fetchView, finishedKey]);

  // Reset iframe-ready whenever the HTML we're about to render changes,
  // so the loader covers re-fetches (e.g. after a PVA view update).
  useEffect(() => {
    setIframeReady(false);
  }, [wrappedHtml]);

  // Hold the spinner back so a fast load goes straight to the page's own
  // skeleton rather than flashing a spinner first.
  useEffect(() => {
    if (!loading && view && iframeReady) {
      setSlowLoad(false);
      return;
    }
    const timer = window.setTimeout(() => setSlowLoad(true), 600);
    return () => window.clearTimeout(timer);
  }, [loading, view, iframeReady]);

  // ── Listen for Pusher view update events ──
  useEffect(() => {
    const channel = pusherContext?.pusherChannel;
    if (!channel) return;

    const handler = (data: ViewUpdatedEvent) => {
      if (data.appId !== appId) return;
      // A new view may have been added — refresh the tab bar regardless.
      fetchViews();
      if (data.slug === activeSlug) fetchView();
    };

    channel.bind(AgentEvents.PROJECT_APP_VIEW_UPDATED, handler);
    return () => {
      channel.unbind(AgentEvents.PROJECT_APP_VIEW_UPDATED, handler);
    };
  }, [pusherContext?.pusherChannel, appId, activeSlug, fetchView, fetchViews]);

  // ── Listen for Pusher state-updated events (realtime state sync) ──
  useEffect(() => {
    const pusherInstance = (pusherContext as any)?.pusherInstance;
    const pusherEnv = process.env.REACT_APP_PUSHER_ENV;
    if (!pusherInstance || !pusherEnv) return;

    const channelName = `${pusherEnv}-project-app-state-${appId}`;
    const channel = pusherInstance.subscribe(channelName);

    channel.bind('state-updated', () => {
      if (iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage(
          { type: 'dream-state-refetch' },
          '*',
        );
      }
    });

    return () => {
      channel.unbind_all();
      pusherInstance.unsubscribe(channelName);
    };
  }, [(pusherContext as any)?.pusherInstance, appId]);

  // ── PostMessage bridge: handle messages from iframe ──
  useEffect(() => {
    const sendAuth = async () => {
      try {
        const token = await getAccessTokenSilently();
        iframeRef.current?.contentWindow?.postMessage(
          { type: 'dream-auth', token },
          '*',
        );
      } catch (err) {
        console.error('Error sending auth token to iframe:', err);
      }
    };

    const handleMessage = (e: MessageEvent) => {
      // Only accept messages from our iframe
      if (!iframeRef.current || e.source !== iframeRef.current.contentWindow)
        return;
      const data = e.data;
      if (!data || typeof data !== 'object') return;

      switch (data.type) {
        case 'dream-iframe-ready':
          sendAuth();
          break;
        case 'dream-navigate':
          if (typeof data.to === 'string' && data.to.startsWith('/')) {
            navigate(data.to);
          }
          break;
        case 'dream-open-artifact':
          if (typeof data.artifactId === 'string' && data.artifactId) {
            setOpenArtifactId(data.artifactId);
          }
          break;
        // Generated pages configure things through chat rather than bespoke
        // forms — hand the wording to the thread composer (ThreadView listens).
        case 'dream-chat-prefill':
          if (typeof data.text === 'string' && data.text) {
            window.dispatchEvent(
              new CustomEvent('fill-chat-input', {
                detail: { text: data.text, autoSend: data.autoSend === true },
              }),
            );
          }
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [getAccessTokenSilently, navigate]);

  // Tab bar across the app's views (home view first). Only shown when the app
  // has more than one view — a single-view app renders no tabs.
  const tabBar =
    views.length > 1 ? (
      <nav className='project-app-tabs' role='tablist'>
        {views.map((v) => {
          const isActive = v.slug === activeSlug;
          return (
            <button
              key={v.slug}
              type='button'
              role='tab'
              aria-selected={isActive}
              className={`project-app-tab${
                isActive ? ' project-app-tab--active' : ''
              }`}
              onClick={() => {
                if (isActive) return;
                setError(null);
                setView(null);
                setActiveSlug(v.slug);
              }}
            >
              {v.name || v.slug}
            </button>
          );
        })}
      </nav>
    ) : null;

  const withTabs = (content: React.ReactNode) => (
    <div className='project-app-view'>
      {tabBar}
      {content}
    </div>
  );

  // View doesn't exist yet (Dream is mid-generation) — show the
  // "Getting your project ready" empty state.
  if (error === 'not-found') {
    return withTabs(
      <div className='project-app-generated-page'>
        <ProjectAppEmptyState building />
      </div>,
    );
  }

  if (error) {
    return withTabs(
      <div className='project-app-generated-page__error'>
        <p>Couldn't load the project page.</p>
        <small>{error}</small>
      </div>,
    );
  }

  // View exists but has no HTML yet — render the empty state instead of a
  // blank transparent iframe.
  if (!loading && view && !view.html?.trim()) {
    return withTabs(
      <div className='project-app-generated-page'>
        <ProjectAppEmptyState building />
      </div>,
    );
  }

  const showLoader = loading || !view || !iframeReady;

  return withTabs(
    <div className='project-app-generated-page'>
      {view && (
        <iframe
          ref={iframeRef}
          title={view.name || 'Project page'}
          srcDoc={wrappedHtml}
          sandbox='allow-scripts allow-same-origin allow-forms allow-popups'
          onLoad={() => setIframeReady(true)}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            display: 'block',
            // Hide the iframe until its document has loaded so we never
            // flash unstyled markup at the user.
            visibility: iframeReady ? 'visible' : 'hidden',
          }}
        />
      )}
      {showLoader && (
        <div className='project-app-generated-page__loading-overlay'>
          {slowLoad && <div className='loading-spinner' />}
        </div>
      )}
      {openArtifactId && (
        <ArtifactOpener
          key={openArtifactId}
          artifactId={openArtifactId}
          hideButton
          defaultOpen
          onClose={() => setOpenArtifactId(null)}
        />
      )}
    </div>,
  );
};

export default ProjectAppGeneratedPage;
